import React, { useState, useEffect } from 'react';
import TutorialLayout from '../../components/TutorialLayout';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getHostname } from '../../utils/getHostname';
import '../../components/DownloadProfileModal.css';
import './Mac.css';

function Windows() {
  const hostname = getHostname();
  const [pritunlDownloadUrl, setPritunlDownloadUrl] = useState(null);
  const [isLoadingDownload, setIsLoadingDownload] = useState(true);
  
  useDocumentTitle('Windows VPN Setup');
  
  useEffect(() => {
    const fetchPritunlDownloadUrl = async () => {
      try {
        const response = await fetch('https://api.github.com/repos/pritunl/pritunl-client-electron/releases/latest');
        const data = await response.json();
        
        if (data.assets && Array.isArray(data.assets)) {
          const exeAsset = data.assets.find(asset => asset.name === 'Pritunl.exe');
          if (exeAsset && exeAsset.browser_download_url) {
            setPritunlDownloadUrl(exeAsset.browser_download_url);
          }
        }
      } catch (error) {
        console.error('Error fetching Pritunl download URL:', error);
      } finally {
        setIsLoadingDownload(false);
      }
    };
    
    fetchPritunlDownloadUrl();
  }, []);
  
  return (
    <TutorialLayout
      title="Windows VPN Setup with Pritunl Client"
      description={`This guide will walk you through downloading and installing the Pritunl client on your Windows PC, and loading your VPN profile zip file from ${hostname}.`}
    >
      <h4>1. Download Your VPN Profile</h4>
      <p>
        After a successful purchase, or when you open the link from your email, you will see a popup like the one below.
        Use it to download your VPN profile.
      </p>
      <ol>
        <li>Click the "Download VPN Profile" button in the popup to download your VPN profile zip file.</li>
        <li>Save the zip file to a location you can easily find, such as your Downloads folder.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/login.png" alt="Download VPN profile popup after purchase or email link" />
      </div>
      <p><strong>Note:</strong> Keep this zip file safe as you'll need it to configure the Pritunl client.</p>

      <h4>2. Download and Install Pritunl Client</h4>
      <p>Now you need to download and install the Pritunl client for Windows:</p>
      <ol>
        <li>
          {pritunlDownloadUrl ? (
            <>
              Click the button below to download the Pritunl client for Windows:
              <div style={{ marginTop: '15px', marginBottom: '15px', maxWidth: '450px' }}>
                <a 
                  href={pritunlDownloadUrl} 
                  className="download-vpn-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download Pritunl Client for Windows
                </a>
              </div>
            </>
          ) : isLoadingDownload ? (
            <span style={{ color: '#718096', fontStyle: 'italic' }}>Loading download link...</span>
          ) : (
            <>
              If the download button is not available, you can visit the{' '}
              <a href="https://client.pritunl.com/" target="_blank" rel="noopener noreferrer">Pritunl Client download page</a>.
            </>
          )}
        </li>
        <li>Once the download is complete, locate the downloaded file (usually in your Downloads folder). It will be a .exe file.</li>
        <li>Double-click the .exe installer file to begin installation.</li>
        <li>Follow the installation wizard to install Pritunl on your Windows PC. You may be prompted by Windows User Account Control (UAC) - click "Yes" to allow the installation.</li>
        <li>If Windows Defender or your antivirus software shows a warning, you can safely allow the application. Pritunl is a legitimate VPN client application.</li>
      </ol>

      <h4>3. Load Your VPN Profile</h4>
      <p>Now that you have Pritunl installed and your VPN profile downloaded, it's time to import the profile:</p>
      <ol>
        <li>Open the Pritunl application from your Start menu or desktop shortcut.</li>
        <li>When Pritunl opens, you'll see the main interface. Look for an "Import" or "Import Profile" button (usually located at the top of the window or in a menu).</li>
        <li>Click the "Import" button and navigate to where you saved your VPN profile zip file.</li>
        <li>Select the zip file and click "Open" or "Import".</li>
        <li>The VPN profile will be imported and should appear in your list of available VPN connections.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/import.png" alt="Importing VPN profile in Pritunl client" />
      </div>
      <p><strong>Alternative method:</strong> You can also drag and drop the zip file directly onto the Pritunl application window to import it.</p>

      <h4>4. Connect to VPN</h4>
      <p>Once your profile is loaded, you can connect to the VPN:</p>
      <ol>
        <li>In the Pritunl client, you should see your imported VPN profile listed.</li>
        <li>Click on the profile name or the "Connect" button next to it.</li>
        <li>Wait for the connection to establish. You'll see a status indicator showing "Connected" when successful.</li>
        <li>The Pritunl client icon in your system tray (bottom-right corner) will also show the connection status.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/connect.png" alt="Connecting to VPN in Pritunl client" />
      </div>

      <h4>5. Verify Your Connection</h4>
      <p>To verify that your VPN connection is working:</p>
      <ol>
        <li>Check the status in the Pritunl client - it should show "Connected".</li>
        <li>You can visit a website like <a href="https://whatismyipaddress.com" target="_blank" rel="noopener noreferrer">whatismyipaddress.com</a> to verify your IP address has changed.</li>
        <li>The system tray icon will also indicate the connection status.</li>
      </ol>

      <h4>6. Disconnect from VPN</h4>
      <p>To disconnect from the VPN:</p>
      <ol>
        <li>Click on the VPN profile in Pritunl and select "Disconnect", or</li>
        <li>Right-click the Pritunl icon in your system tray and select "Disconnect".</li>
      </ol>

      <h4>7. Enjoy {hostname}</h4>
      <p>You're all set! Once connected, all your internet traffic will be routed through the {hostname} VPN server, providing you with secure and private browsing. You can connect and disconnect anytime using the Pritunl client.</p>
      
      <p><strong>Tips:</strong></p>
      <ul>
        <li>You can set Pritunl to start automatically when Windows starts by going to Settings → Apps → Startup and enabling Pritunl, or by adding it to your Startup folder.</li>
        <li>If you encounter any connection issues, make sure your VPN profile zip file is not corrupted and that you're using the correct login credentials.</li>
        <li>Keep your VPN profile zip file in a secure location in case you need to reimport it later.</li>
      </ul>
    </TutorialLayout>
  );
}

export default Windows;
